import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VoyageHistoryTable = ({ vessel, voyageHistory }) => {
  const [sortField, setSortField] = useState('departureDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVoyages, setSelectedVoyages] = useState(new Set());

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectVoyage = (voyageId) => {
    const newSelected = new Set(selectedVoyages);
    if (newSelected?.has(voyageId)) {
      newSelected?.delete(voyageId);
    } else {
      newSelected?.add(voyageId);
    }
    setSelectedVoyages(newSelected);
  };

  const filteredVoyages = voyageHistory?.filter(voyage =>
    voyage?.route?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    voyage?.voyageNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    voyage?.charterer?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedVoyages?.size === filteredVoyages?.length) {
      setSelectedVoyages(new Set());
    } else {
      setSelectedVoyages(new Set(filteredVoyages.map(v => v.id)));
    }
  };

  const sortedVoyages = [...filteredVoyages]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'departureDate' || sortField === 'arrivalDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'text-success bg-success/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      case 'non-compliant':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getCIIColor = (rating) => {
    switch (rating) {
      case 'A':
        return 'text-success bg-success/10';
      case 'B':
        return 'text-success bg-success/10';
      case 'C':
        return 'text-warning bg-warning/10';
      case 'D':
        return 'text-error bg-error/10';
      case 'E':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" strokeWidth={2} />;
    }
    return (
      <Icon 
        name={sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-foreground" 
        strokeWidth={2} 
      />
    );
  };

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Voyage History</h3>
          <p className="text-sm text-muted-foreground">
            Historical performance data and emissions tracking
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Input
            type="search"
            placeholder="Search voyages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-64"
          />
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
      {selectedVoyages?.size > 0 && (
        <div className="mb-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedVoyages?.size} voyage{selectedVoyages?.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" iconName="BarChart3" iconPosition="left">
                Compare
              </Button>
              <Button variant="ghost" size="sm" iconName="FileText" iconPosition="left">
                Generate Report
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3">
                <input
                  type="checkbox"
                  checked={selectedVoyages?.size === filteredVoyages?.length && filteredVoyages?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('voyageNumber')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent transition-maritime"
                >
                  <span>Voyage</span>
                  <SortIcon field="voyageNumber" />
                </button>
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('route')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent transition-maritime"
                >
                  <span>Route</span>
                  <SortIcon field="route" />
                </button>
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('departureDate')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent transition-maritime"
                >
                  <span>Period</span>
                  <SortIcon field="departureDate" />
                </button>
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('fuelConsumption')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent transition-maritime"
                >
                  <span>Fuel (MT)</span>
                  <SortIcon field="fuelConsumption" />
                </button>
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('co2Emissions')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent transition-maritime"
                >
                  <span>CO₂ (MT)</span>
                  <SortIcon field="co2Emissions" />
                </button>
              </th>
              <th className="text-left p-3">
                <span className="text-sm font-medium text-foreground">CII Rating</span>
              </th>
              <th className="text-left p-3">
                <span className="text-sm font-medium text-foreground">Compliance</span>
              </th>
              <th className="text-left p-3">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedVoyages?.map((voyage) => (
              <tr key={voyage?.id} className="border-b border-border hover:bg-muted/50 transition-maritime">
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedVoyages?.has(voyage?.id)}
                    onChange={() => handleSelectVoyage(voyage?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div>
                    <div className="font-medium text-foreground">{voyage?.voyageNumber}</div>
                    <div className="text-sm text-muted-foreground">{voyage?.charterer}</div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-sm text-foreground">{voyage?.route}</div>
                </td>
                <td className="p-3">
                  <div className="text-sm text-foreground">
                    {new Date(voyage.departureDate)?.toLocaleDateString()} - {new Date(voyage.arrivalDate)?.toLocaleDateString()}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {voyage?.duration} days
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-sm font-medium text-foreground">
                    {voyage?.fuelConsumption?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(voyage?.fuelConsumption / voyage?.duration)?.toFixed(1)}/day
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-sm font-medium text-foreground">
                    {voyage?.co2Emissions?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {voyage?.emissionsIntensity?.toFixed(1)} gCO₂/t·nm
                  </div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCIIColor(voyage?.ciiRating)}`}>
                    {voyage?.ciiRating}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplianceColor(voyage?.complianceStatus)}`}>
                    {voyage?.complianceStatus === 'compliant' ? 'Compliant' : 
                     voyage?.complianceStatus === 'warning' ? 'Warning' : 'Non-Compliant'}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" iconName="FileText">
                      Report
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedVoyages?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1} />
          <h4 className="text-lg font-medium text-foreground mb-2">No voyages found</h4>
          <p className="text-sm text-muted-foreground">
            {searchTerm ? 'Try adjusting your search criteria' : 'No voyage history available for this vessel'}
          </p>
        </div>
      )}
      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {sortedVoyages?.length} of {voyageHistory?.length} voyages
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" iconName="ChevronLeft">
            Previous
          </Button>
          <Button variant="ghost" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoyageHistoryTable;